const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Diagram 1: Gradient, Divergence & Curl side-by-side ───────────
function DiagramDelOperator() {
  const W = 900, H = 380;

  // Panel dimensions
  const PW = (W - 28) / 3;
  const p = i => 8 + i * (PW + 6);

  // ── Panel 1: Gradient field (scalar → vector) ──
  // f(x,y) = x²+y², gradient points radially outward
  const gc = [p(0) + PW / 2, 195], gsc = 38;
  const gTx = x => gc[0] + x * gsc, gTy = y => gc[1] - y * gsc;
  const gradArrows = [];
  for (let xi = -2; xi <= 2; xi++) {
    for (let yi = -2; yi <= 2; yi++) {
      if (xi === 0 && yi === 0) continue;
      const len = Math.sqrt(xi*xi + yi*yi);
      const scale = 0.55 / (len + 0.2);
      gradArrows.push({ xi, yi, fx: xi * scale * 2, fy: yi * scale * 2 });
    }
  }

  // ── Panel 2: Divergence (source field) ──
  const dc = [p(1) + PW / 2, 195], dsc = 38;
  const dTx = x => dc[0] + x * dsc, dTy = y => dc[1] - y * dsc;
  const divArrows = [];
  for (let xi = -2.5; xi <= 2.5; xi += 1) {
    for (let yi = -2.5; yi <= 2.5; yi += 1) {
      const len = Math.sqrt(xi*xi + yi*yi);
      const scale = 0.48 / (len + 0.5);
      divArrows.push({ xi, yi, fx: xi * scale * 1.8, fy: yi * scale * 1.8 });
    }
  }

  // ── Panel 3: Curl (rotational field) ──
  const cc = [p(2) + PW / 2, 195], csc = 38;
  const cTx = x => cc[0] + x * csc, cTy = y => cc[1] - y * csc;
  const curlArrows = [];
  for (let xi = -2.5; xi <= 2.5; xi += 1) {
    for (let yi = -2.5; yi <= 2.5; yi += 1) {
      const len = Math.sqrt(xi*xi + yi*yi);
      const scale = 0.48 / (len + 0.4);
      curlArrows.push({ xi, yi, fx: -yi * scale * 1.8, fy: xi * scale * 1.8 });
    }
  }

  const Arrow = ({ x0, y0, x1, y1, color }) => {
    const dx = x1 - x0, dy = y1 - y0;
    const ang = Math.atan2(dy, dx);
    const hx = x1 - 7 * Math.cos(ang), hy = y1 - 7 * Math.sin(ang);
    return (
      <g>
        <line x1={x0} y1={y0} x2={hx} y2={hy} stroke={color} strokeWidth="1.5" />
        <polygon
          points={`${x1},${y1} ${x1-8*Math.cos(ang-0.45)},${y1-8*Math.sin(ang-0.45)} ${x1-8*Math.cos(ang+0.45)},${y1-8*Math.sin(ang+0.45)}`}
          fill={color}
        />
      </g>
    );
  };

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>The Del Operator ∇ — Gradient, Divergence, and Curl</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {[0, 1, 2].map(i => (
          <rect key={i} x={p(i)} y={8} width={PW} height={H - 16} rx={5} fill="#fff" stroke="#ccd9ea" />
        ))}

        {/* ── Panel 1: Gradient ── */}
        <text x={p(0)+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Gradient  ∇f</text>
        <text x={p(0)+PW/2} y={44} textAnchor="middle" fontSize="11" fill="#555" fontStyle="italic">scalar field → vector field</text>
        {[-2,-1,0,1,2].map(v => (
          <g key={v}>
            <line x1={gTx(v)} y1={gTy(-3)} x2={gTx(v)} y2={gTy(3)} stroke="#f0f0f0" strokeWidth="1"/>
            <line x1={gTx(-3)} y1={gTy(v)} x2={gTx(3)} y2={gTy(v)} stroke="#f0f0f0" strokeWidth="1"/>
          </g>
        ))}
        <line x1={gTx(-2.8)} y1={gc[1]} x2={gTx(2.8)} y2={gc[1]} stroke="#1F3864" strokeWidth="1.3"/>
        <line x1={gc[0]} y1={gTy(-2.8)} x2={gc[0]} y2={gTy(2.8)} stroke="#1F3864" strokeWidth="1.3"/>
        {/* Scalar field contours (circles for f=x²+y²) */}
        {[1,1.8,2.6].map((r,i) => (
          <circle key={i} cx={gc[0]} cy={gc[1]} r={r*gsc} fill="none" stroke="#cce0f5" strokeWidth="1.2" strokeDasharray="4,3"/>
        ))}
        {gradArrows.map(({xi,yi,fx,fy},i) => (
          <Arrow key={i} x0={gTx(xi)} y0={gTy(yi)} x1={gTx(xi+fx)} y1={gTy(yi+fy)} color="#2E75B6"/>
        ))}
        <rect x={p(0)+8} y={H-88} width={PW-16} height={72} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={p(0)+16} y={H-72} fontSize="12" fill="#1F3864" fontWeight="700">∇f = (∂f/∂x, ∂f/∂y, ∂f/∂z)</text>
        <text x={p(0)+16} y={H-55} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">f(x,y) = x²+y²</text>
        <text x={p(0)+16} y={H-38} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">∇f = (2x, 2y)  — radial</text>
        <text x={p(0)+16} y={H-22} fontSize="11" fill="#555">⊥ to level curves (dashed)</text>

        {/* ── Panel 2: Divergence ── */}
        <text x={p(1)+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Divergence  ∇·F</text>
        <text x={p(1)+PW/2} y={44} textAnchor="middle" fontSize="11" fill="#555" fontStyle="italic">vector field → scalar field</text>
        {[-2,-1,0,1,2].map(v => (
          <g key={v}>
            <line x1={dTx(v)} y1={dTy(-3)} x2={dTx(v)} y2={dTy(3)} stroke="#f0f0f0" strokeWidth="1"/>
            <line x1={dTx(-3)} y1={dTy(v)} x2={dTx(3)} y2={dTy(v)} stroke="#f0f0f0" strokeWidth="1"/>
          </g>
        ))}
        <line x1={dTx(-2.8)} y1={dc[1]} x2={dTx(2.8)} y2={dc[1]} stroke="#1F3864" strokeWidth="1.3"/>
        <line x1={dc[0]} y1={dTy(-2.8)} x2={dc[0]} y2={dTy(2.8)} stroke="#1F3864" strokeWidth="1.3"/>
        {/* Source circle indicator */}
        <circle cx={dc[0]} cy={dc[1]} r={8} fill="#27ae60" opacity="0.3"/>
        <text x={dc[0]} y={dc[1]+4} textAnchor="middle" fontSize="10" fill="#27ae60" fontWeight="700">src</text>
        {divArrows.map(({xi,yi,fx,fy},i) => (
          <Arrow key={i} x0={dTx(xi)} y0={dTy(yi)} x1={dTx(xi+fx)} y1={dTy(yi+fy)} color="#27ae60"/>
        ))}
        <rect x={p(1)+8} y={H-88} width={PW-16} height={72} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={p(1)+16} y={H-72} fontSize="12" fill="#1F3864" fontWeight="700">∇·F = ∂P/∂x + ∂Q/∂y + ∂R/∂z</text>
        <text x={p(1)+16} y={H-55} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">div F &gt; 0 → source (flow out)</text>
        <text x={p(1)+16} y={H-38} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">div F &lt; 0 → sink (flow in)</text>
        <text x={p(1)+16} y={H-22} fontSize="11" fill="#555">div F = 0 → incompressible fluid</text>

        {/* ── Panel 3: Curl ── */}
        <text x={p(2)+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Curl  ∇×F</text>
        <text x={p(2)+PW/2} y={44} textAnchor="middle" fontSize="11" fill="#555" fontStyle="italic">vector field → vector field</text>
        {[-2,-1,0,1,2].map(v => (
          <g key={v}>
            <line x1={cTx(v)} y1={cTy(-3)} x2={cTx(v)} y2={cTy(3)} stroke="#f0f0f0" strokeWidth="1"/>
            <line x1={cTx(-3)} y1={cTy(v)} x2={cTx(3)} y2={cTy(v)} stroke="#f0f0f0" strokeWidth="1"/>
          </g>
        ))}
        <line x1={cTx(-2.8)} y1={cc[1]} x2={cTx(2.8)} y2={cc[1]} stroke="#1F3864" strokeWidth="1.3"/>
        <line x1={cc[0]} y1={cTy(-2.8)} x2={cc[0]} y2={cTy(2.8)} stroke="#1F3864" strokeWidth="1.3"/>
        {/* Rotation circle */}
        {[0.7,1.4,2.1].map((r,i) => {
          const pts = [];
          for (let a=0; a<=2*Math.PI+0.1; a+=0.2) pts.push([cTx(r*Math.cos(a)),cTy(r*Math.sin(a))]);
          const d = pts.map((pt,k) => `${k===0?'M':'L'}${pt[0].toFixed(1)},${pt[1].toFixed(1)}`).join(' ');
          return <path key={i} d={d} fill="none" stroke="#c0392b" strokeWidth="1" strokeDasharray="3,3" opacity="0.5"/>;
        })}
        {curlArrows.map(({xi,yi,fx,fy},i) => (
          <Arrow key={i} x0={cTx(xi)} y0={cTy(yi)} x1={cTx(xi+fx)} y1={cTy(yi+fy)} color="#c0392b"/>
        ))}
        <rect x={p(2)+8} y={H-88} width={PW-16} height={72} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1"/>
        <text x={p(2)+16} y={H-72} fontSize="12" fill="#1F3864" fontWeight="700">∇×F = (∂R/∂y−∂Q/∂z, ∂P/∂z−∂R/∂x,</text>
        <text x={p(2)+16} y={H-56} fontSize="12" fill="#1F3864" fontWeight="700">              ∂Q/∂x−∂P/∂y)</text>
        <text x={p(2)+16} y={H-38} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">|curl F| = angular speed × 2</text>
        <text x={p(2)+16} y={H-22} fontSize="11" fill="#555">curl F = 0 → irrotational field</text>
      </svg>
      <div style={S.dCaption}>
        The del operator ∇ is the vector of partial derivatives (∂/∂x, ∂/∂y, ∂/∂z). Applied to a scalar field it produces the gradient (steepest ascent vector). Dotted into a vector field it gives divergence (net outflow per unit volume). Crossed into a vector field it gives curl (local rotation axis and rate). These three operations are the backbone of all field theories in engineering and physics.
      </div>
    </div>
  );
}

// ── Diagram 2: Surface Integrals & Flux ───────────────────────────
function DiagramSurfaceIntegral() {
  const W = 900, H = 370;
  const ox = 155, oy = 265;
  const isoX = (x,y,z) => ox + x*46 - y*30;
  const isoY = (x,y,z) => oy - z*40 + y*19;

  // Paraboloid cap surface
  const surf = (x,y) => 2.6 - 0.45*(x*x+y*y);
  const surfPts = (xi, step=0.3) => {
    const pts = [];
    for (let yi=-2.2; yi<=2.2; yi+=step) {
      const z = surf(xi, yi);
      if (z > 0) pts.push([isoX(xi,yi,z), isoY(xi,yi,z)]);
    }
    return pts;
  };

  // Normal vectors on surface at several points
  const normals = [
    {x:0,y:0}, {x:1,y:0}, {x:-1,y:0}, {x:0,y:1}, {x:0.7,y:0.7},
  ].map(({x,y}) => {
    const z = surf(x,y);
    // Normal to paraboloid z=c-r²: n = (2x*a, 2y*a, 1) normalised roughly
    const nx = 0.9*x, ny = 0.9*y, nz = 1.4;
    const len = Math.sqrt(nx*nx+ny*ny+nz*nz);
    const scale = 0.55;
    return { x,y,z, nx:nx/len*scale, ny:ny/len*scale, nz:nz/len*scale };
  });

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Surface Integrals & Flux — Flow Through a Surface ∬_S F·dS</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Left: 3D surface with normals */}
        <rect x={8} y={8} width={440} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={228} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Outward Normal Vectors on a Surface</text>

        {/* Surface grid Y lines */}
        {[-2,-1.5,-1,-0.5,0,0.5,1,1.5,2].map(xi => {
          const pts = surfPts(xi, 0.25);
          if(pts.length < 2) return null;
          return <path key={'sy'+xi} d={pts.map((p,k)=>`${k===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')} fill="none" stroke="#b8d4f0" strokeWidth="1"/>;
        })}
        {[-2,-1.5,-1,-0.5,0,0.5,1,1.5,2].map(yi => {
          const pts = [];
          for(let xi=-2.2; xi<=2.2; xi+=0.25){
            const z=surf(xi,yi); if(z>0) pts.push([isoX(xi,yi,z),isoY(xi,yi,z)]);
          }
          if(pts.length<2) return null;
          return <path key={'sx'+yi} d={pts.map((p,k)=>`${k===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')} fill="none" stroke="#cce0f5" strokeWidth="1.2"/>;
        })}

        {/* Normal vectors */}
        {normals.map(({x,y,z,nx,ny,nz},i)=>{
          const x0=isoX(x,y,z), y0=isoY(x,y,z);
          const x1=isoX(x+nx,y+ny,z+nz), y1=isoY(x+nx,y+ny,z+nz);
          const ang=Math.atan2(y1-y0,x1-x0);
          const hx=x1-8*Math.cos(ang), hy=y1-8*Math.sin(ang);
          return (
            <g key={i}>
              <line x1={x0} y1={y0} x2={hx} y2={hy} stroke="#c0392b" strokeWidth="2"/>
              <polygon points={`${x1},${y1} ${x1-9*Math.cos(ang-0.4)},${y1-9*Math.sin(ang-0.4)} ${x1-9*Math.cos(ang+0.4)},${y1-9*Math.sin(ang+0.4)}`} fill="#c0392b"/>
              <circle cx={x0} cy={y0} r={3} fill="#1F3864"/>
            </g>
          );
        })}
        <text x={isoX(0.15,0.15,surf(0,0))-34} y={isoY(0.15,0.15,surf(0,0))-14} fontSize="13" fill="#c0392b" fontWeight="700">n̂ (outward)</text>

        {/* Axes */}
        {[[[3,0,0],[0,0,0],'x'],[[0,2.8,0],[0,0,0],'y'],[[0,0,3.2],[0,0,0],'z']].map(([[x1,y1,z1],[x0,y0,z0],label],i)=>(
          <g key={i}>
            <line x1={isoX(x0,y0,z0)} y1={isoY(x0,y0,z0)} x2={isoX(x1,y1,z1)} y2={isoY(x1,y1,z1)} stroke="#1F3864" strokeWidth="1.5"/>
            <text x={isoX(x1+0.2,y1,z1)} y={isoY(x1,y1+0.15,z1)+4} fontSize="13" fill="#1F3864" fontWeight="700">{label}</text>
          </g>
        ))}

        {/* Right: formula panels */}
        <rect x={456} y={8} width={436} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={674} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Surface Integral Concepts</text>

        {[
          {y:40, bg:'#EBF3FB', bc:'#2E75B6', title:'Scalar Surface Integral', lines:['∬_S f(x,y,z) dS','dS = |r_u × r_v| du dv  (area element)','Integrates a scalar quantity over a surface']},
          {y:134, bg:'#f0fff4', bc:'#27ae60', title:'Flux Integral (vector)', lines:['∬_S F · dS = ∬_S F · n̂ dS','n̂ = outward unit normal to surface','Measures net flow of F through surface S']},
          {y:228, bg:'#fff7ee', bc:'#e07b00', title:'Parametric Surface', lines:['r(u,v) = (x(u,v), y(u,v), z(u,v))','dS = r_u × r_v du dv','r_u = ∂r/∂u,  r_v = ∂r/∂v']},
          {y:316, bg:'#fff0f0', bc:'#c0392b', title:'Orientation', lines:['Choice of outward vs inward normal','Closed surface: outward normal is standard','Reversal flips sign of flux integral']},
        ].map(({y,bg,bc,title,lines})=>(
          <g key={title}>
            <rect x={464} y={y} width={420} height={82} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={472} y={y+15} fontSize="13" fill="#1F3864" fontWeight="700">{title}</text>
            {lines.map((ln,i)=><text key={i} x={472} y={y+31+i*17} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">{ln}</text>)}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        A surface integral extends integration from curves to surfaces. The scalar form integrates a function over the surface area. The vector (flux) form measures how much of a field passes through the surface — critical in electromagnetics (electric flux, Gauss's law) and fluid mechanics (mass flow rate through a cross-section).
      </div>
    </div>
  );
}

// ── Diagram 3: Divergence Theorem ─────────────────────────────────
function DiagramDivergenceTheorem() {
  const W = 900, H = 360;

  // Left: 3D sphere with outward arrows
  const cx3 = 180, cy3 = 185, r3d = 110;
  const isoX = (x,y,z) => cx3 + x*46 - y*28;
  const isoY = (x,y,z) => cy3 - z*40 + y*17;

  // Sphere surface points (equator + two latitudes)
  const sphereCircles = [0, 0.5, -0.5].map(lat => {
    const r = Math.cos(lat * Math.PI/2) * r3d;
    const yoff = Math.sin(lat * Math.PI/2) * r3d * 0.7;
    return {r, yoff, lat};
  });

  // Flux arrows on sphere surface
  const fluxArrows = [];
  for(let theta=0; theta<2*Math.PI; theta+=Math.PI/5){
    for(let phi=0.3; phi<Math.PI; phi+=Math.PI/4){
      const x=Math.sin(phi)*Math.cos(theta)*0.9;
      const y=Math.sin(phi)*Math.sin(theta)*0.9;
      const z=Math.cos(phi)*0.9;
      fluxArrows.push({x,y,z, nx:x*0.35, ny:y*0.35, nz:z*0.35});
    }
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Divergence Theorem — Connecting Volume to Surface ∭∇·F dV = ∯F·dS</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Left panel */}
        <rect x={8} y={8} width={440} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={228} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Flux Out of a Closed Volume</text>

        {/* Sphere silhouette */}
        <circle cx={cx3} cy={cy3} r={r3d} fill="#d0e8ff" fillOpacity="0.25" stroke="#2E75B6" strokeWidth="2"/>
        {/* Back half ellipse */}
        <ellipse cx={cx3} cy={cy3} rx={r3d} ry={r3d*0.28} fill="none" stroke="#2E75B6" strokeWidth="1" strokeDasharray="5,3" opacity="0.5"/>
        {/* Front half ellipse */}
        <path d={`M ${cx3-r3d},${cy3} A ${r3d} ${r3d*0.28} 0 0 0 ${cx3+r3d},${cy3}`} fill="none" stroke="#2E75B6" strokeWidth="1.5"/>

        {/* Source dot at center */}
        <circle cx={cx3} cy={cy3} r={7} fill="#27ae60"/>
        <text x={cx3+10} y={cy3+4} fontSize="12" fill="#27ae60" fontWeight="700">div F &gt; 0</text>

        {/* Outward flux arrows */}
        {fluxArrows.map(({x,y,z,nx,ny,nz},i)=>{
          const x0=isoX(x,y,z), y0=isoY(x,y,z);
          const x1=isoX(x+nx,y+ny,z+nz), y1=isoY(x+nx,y+ny,z+nz);
          if(y0<40||y0>H-40||x0<20||x0>430) return null;
          const ang=Math.atan2(y1-y0,x1-x0);
          const hx=x1-7*Math.cos(ang), hy=y1-7*Math.sin(ang);
          return (
            <g key={i}>
              <line x1={x0} y1={y0} x2={hx} y2={hy} stroke="#c0392b" strokeWidth="1.8"/>
              <polygon points={`${x1},${y1} ${x1-8*Math.cos(ang-0.45)},${y1-8*Math.sin(ang-0.45)} ${x1-8*Math.cos(ang+0.45)},${y1-8*Math.sin(ang+0.45)}`} fill="#c0392b"/>
            </g>
          );
        })}

        {/* Label */}
        <text x={cx3} y={cy3+r3d+22} textAnchor="middle" fontSize="12" fill="#2E75B6" fontWeight="700">closed surface S</text>
        <text x={cx3} y={cy3+r3d+38} textAnchor="middle" fontSize="11" fill="#555">enclosing volume V</text>

        {/* Equivalence arrow */}
        <text x={228} y={H-44} textAnchor="middle" fontSize="20" fill="#1F3864" fontWeight="700">⟺</text>
        <text x={228} y={H-22} textAnchor="middle" fontSize="12" fill="#555" fontStyle="italic">Total outward flux = integral of divergence over V</text>

        {/* Right panel */}
        <rect x={456} y={8} width={436} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={674} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Statement &amp; Applications</text>

        {[
          {y:40, bg:'#EBF3FB', bc:'#2E75B6', title:'Divergence Theorem (Gauss)', lines:['∯_S F·dS = ∭_V (∇·F) dV','S = closed surface bounding V','Converts surface integral ↔ volume integral']},
          {y:134, bg:'#f0fff4', bc:'#27ae60', title:"Gauss's Law (Electrostatics)", lines:['∯_S E·dS = Q_enc / ε₀','Flux of E through closed surface','= enclosed charge / permittivity']},
          {y:228, bg:'#fff7ee', bc:'#e07b00', title:'Continuity Equation (Fluids)', lines:['∂ρ/∂t + ∇·(ρv) = 0','Divergence of mass flux = rate of','density change — conservation of mass']},
          {y:310, bg:'#fff0f0', bc:'#c0392b', title:'Engineering Uses', lines:['FEA heat flow, pressure vessels','CFD mass balance over control volumes','EM — flux through any enclosing surface']},
        ].map(({y,bg,bc,title,lines})=>(
          <g key={title}>
            <rect x={464} y={y} width={420} height={80} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={472} y={y+15} fontSize="13" fill="#1F3864" fontWeight="700">{title}</text>
            {lines.map((ln,i)=><text key={i} x={472} y={y+31+i*17} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">{ln}</text>)}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The Divergence Theorem states that the total flux of a vector field outward through a closed surface equals the volume integral of its divergence inside. This is why in fluid mechanics, if divergence is zero everywhere inside a control volume (incompressible flow), there is zero net mass flux through the boundary.
      </div>
    </div>
  );
}

// ── Diagram 4: Stokes' Theorem ─────────────────────────────────────
function DiagramStokesTheorem() {
  const W = 900, H = 370;
  const ox = 155, oy = 265;
  const isoX = (x,y,z) => ox + x*46 - y*30;
  const isoY = (x,y,z) => oy - z*40 + y*19;

  // Tilted flat surface patch (parallelogram)
  const surf = (x,y) => 0.5 + 0.25*x + 0.18*y;
  const corners = [[0,0],[2,0],[2,2],[0,2]].map(([x,y])=>({x,y,z:surf(x,y)}));

  // Curl arrows on surface (out-of-plane)
  const curlPts = [[0.5,0.5],[1,0.5],[1.5,0.5],[0.5,1],[1,1],[1.5,1],[0.5,1.5],[1,1.5],[1.5,1.5]];

  // Boundary path (counterclockwise)
  const boundary = [...corners, corners[0]];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Stokes' Theorem — Circulation Around Boundary = Curl Through Surface ∮F·dr = ∬(∇×F)·dS</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Left panel */}
        <rect x={8} y={8} width={440} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={228} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Curl Through a Surface Bounded by Curve C</text>

        {/* Surface fill */}
        <polygon
          points={corners.map(c=>`${isoX(c.x,c.y,c.z)},${isoY(c.x,c.y,c.z)}`).join(' ')}
          fill="#d0e8ff" fillOpacity="0.5" stroke="#2E75B6" strokeWidth="0"
        />
        {/* Surface grid */}
        {[0.5,1,1.5].map(xi=>{
          const pts=[[xi,0],[xi,2]].map(([x,y])=>[isoX(x,y,surf(x,y)),isoY(x,y,surf(x,y))]);
          return <line key={'gx'+xi} x1={pts[0][0]} y1={pts[0][1]} x2={pts[1][0]} y2={pts[1][1]} stroke="#9ab8d8" strokeWidth="1" strokeDasharray="4,3"/>;
        })}
        {[0.5,1,1.5].map(yi=>{
          const pts=[[0,yi],[2,yi]].map(([x,y])=>[isoX(x,y,surf(x,y)),isoY(x,y,surf(x,y))]);
          return <line key={'gy'+yi} x1={pts[0][0]} y1={pts[0][1]} x2={pts[1][0]} y2={pts[1][1]} stroke="#9ab8d8" strokeWidth="1" strokeDasharray="4,3"/>;
        })}

        {/* Boundary curve C with direction arrows */}
        {boundary.slice(0,-1).map((c,i)=>{
          const next=boundary[i+1];
          const x0=isoX(c.x,c.y,c.z), y0=isoY(c.x,c.y,c.z);
          const x1=isoX(next.x,next.y,next.z), y1=isoY(next.x,next.y,next.z);
          const mx=(x0+x1)/2, my=(y0+y1)/2;
          const ang=Math.atan2(y1-y0,x1-x0);
          return (
            <g key={i}>
              <line x1={x0} y1={y0} x2={x1} y2={y1} stroke="#c0392b" strokeWidth="2.8"/>
              <polygon points={`${mx},${my} ${mx-10*Math.cos(ang-0.45)},${my-10*Math.sin(ang-0.45)} ${mx-10*Math.cos(ang+0.45)},${my-10*Math.sin(ang+0.45)}`} fill="#c0392b"/>
            </g>
          );
        })}
        {corners.map((c,i)=>(
          <circle key={i} cx={isoX(c.x,c.y,c.z)} cy={isoY(c.x,c.y,c.z)} r={4} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
        ))}
        <text x={isoX(0,0,surf(0,0))-12} y={isoY(0,0,surf(0,0))+4} fontSize="14" fill="#c0392b" fontWeight="700">C</text>

        {/* Curl (normal) arrows on surface */}
        {curlPts.map(([x,y],i)=>{
          const z=surf(x,y);
          const x0=isoX(x,y,z), y0=isoY(x,y,z);
          // Normal roughly straight up in iso projection
          const x1=isoX(x,y,z+0.55), y1=isoY(x,y,z+0.55);
          const ang=Math.atan2(y1-y0,x1-x0);
          const hx=x1-6*Math.cos(ang), hy=y1-6*Math.sin(ang);
          return (
            <g key={i}>
              <line x1={x0} y1={y0} x2={hx} y2={hy} stroke="#27ae60" strokeWidth="1.8"/>
              <polygon points={`${x1},${y1} ${x1-7*Math.cos(ang-0.45)},${y1-7*Math.sin(ang-0.45)} ${x1-7*Math.cos(ang+0.45)},${y1-7*Math.sin(ang+0.45)}`} fill="#27ae60"/>
            </g>
          );
        })}
        <text x={isoX(1,1,surf(1,1)+0.8)} y={isoY(1,1,surf(1,1)+0.8)-8} fontSize="12" fill="#27ae60" fontWeight="700">∇×F</text>

        {/* Axes */}
        <line x1={ox} y1={oy} x2={isoX(3.2,0,0)} y2={isoY(3.2,0,0)} stroke="#1F3864" strokeWidth="1.5"/>
        <line x1={ox} y1={oy} x2={isoX(0,2.8,0)} y2={isoY(0,2.8,0)} stroke="#1F3864" strokeWidth="1.5"/>
        <line x1={ox} y1={oy} x2={isoX(0,0,3)} y2={isoY(0,0,3)} stroke="#1F3864" strokeWidth="1.5"/>
        <text x={isoX(3.3,0,0)} y={isoY(3.3,0,0)+4} fontSize="13" fill="#1F3864" fontWeight="700">x</text>
        <text x={isoX(0,2.9,0)-12} y={isoY(0,2.9,0)+4} fontSize="13" fill="#1F3864" fontWeight="700">y</text>
        <text x={isoX(0,0,3.1)-6} y={isoY(0,0,3.1)-4} fontSize="13" fill="#1F3864" fontWeight="700">z</text>

        {/* Right panel */}
        <rect x={456} y={8} width={436} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={674} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Statement &amp; Connections</text>

        {[
          {y:40, bg:'#EBF3FB', bc:'#2E75B6', title:"Stokes' Theorem", lines:["∮_C F·dr = ∬_S (∇×F)·dS","C = boundary curve of surface S","Line integral of F = flux of curl through S"]},
          {y:134, bg:'#f0fff4', bc:'#27ae60', title:'Right-Hand Rule for Orientation', lines:['Curl fingers in direction of C traversal','Thumb points in direction of positive normal n̂','Ensures consistent sign convention']},
          {y:228, bg:'#fff7ee', bc:'#e07b00', title:'Special Case: Green\'s Theorem', lines:['Stokes in 2D (flat surface in xy-plane)','∮_C (P dx+Q dy) = ∬_R (∂Q/∂x−∂P/∂y) dA','Green\'s Thm is Stokes with n̂ = k̂']},
          {y:310, bg:'#fff0f0', bc:'#c0392b', title:'Conservative Fields & Curl', lines:['If ∇×F = 0 everywhere → F is conservative','F = ∇φ for some potential φ','∮_C F·dr = 0 for all closed paths C']},
        ].map(({y,bg,bc,title,lines})=>(
          <g key={title}>
            <rect x={464} y={y} width={420} height={80} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={472} y={y+15} fontSize="13" fill="#1F3864" fontWeight="700">{title}</text>
            {lines.map((ln,i)=><text key={i} x={472} y={y+31+i*17} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">{ln}</text>)}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Stokes' Theorem relates the circulation of a vector field around a closed curve C to the flux of its curl through any surface S bounded by C. The direction of traversal and the normal to S are linked by the right-hand rule. This theorem underlies Faraday's law of induction — the circulation of the electric field around a loop equals the rate of change of magnetic flux through it.
      </div>
    </div>
  );
}

// ── Diagram 5: The Four Fundamental Theorems — Overview ───────────
function DiagramFundamentalTheorems() {
  const W = 900, H = 340;
  const cards = [
    {
      x: 14, w: 200, bg: '#EBF3FB', bc: '#2E75B6', color: '#2E75B6',
      title: 'FTC (1D)',
      sub: '∫_a^b f\'(x)dx = f(b)-f(a)',
      desc: 'Integral of derivative\n= boundary values',
      dim: '1D — interval [a,b]',
      shape: 'line'
    },
    {
      x: 228, w: 200, bg: '#f0fff4', bc: '#27ae60', color: '#27ae60',
      title: "Green's Thm (2D)",
      sub: '∮_C F·dr = ∬_R (∇×F)·k̂ dA',
      desc: 'Line integral around\nboundary C of region R',
      dim: '2D — region + boundary curve',
      shape: 'rect'
    },
    {
      x: 442, w: 200, bg: '#fff7ee', bc: '#e07b00', color: '#e07b00',
      title: "Stokes' Thm (3D)",
      sub: '∮_C F·dr = ∬_S (∇×F)·dS',
      desc: 'Line integral around\nboundary C of surface S',
      dim: '3D — surface + boundary curve',
      shape: 'surf'
    },
    {
      x: 656, w: 230, bg: '#fff0f0', bc: '#c0392b', color: '#c0392b',
      title: 'Divergence Thm (3D)',
      sub: '∯_S F·dS = ∭_V (∇·F)dV',
      desc: 'Surface flux through\nboundary S of volume V',
      dim: '3D — volume + boundary surface',
      shape: 'sphere'
    },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>The Four Fundamental Theorems of Vector Calculus — Unifying Pattern</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Unifying label */}
        <rect x={14} y={8} width={872} height={30} rx={4} fill="#1F3864" fillOpacity="0.06"/>
        <text x={450} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          All four theorems share one idea:  ∫_interior (derivative of F) = ∫_boundary (F)
        </text>

        {cards.map(({x,w,bg,bc,color,title,sub,desc,dim,shape})=>(
          <g key={title}>
            <rect x={x} y={46} width={w} height={H-54} rx={6} fill={bg} stroke={bc} strokeWidth="2"/>
            {/* Mini diagram */}
            <rect x={x+8} y={54} width={w-16} height={90} rx={4} fill="#fff" fillOpacity="0.6"/>
            {shape==='line' && <>
              <line x1={x+20} y1={99} x2={x+w-20} y2={99} stroke={color} strokeWidth="3"/>
              <circle cx={x+20} cy={99} r={5} fill={color}/>
              <circle cx={x+w-20} cy={99} r={5} fill={color}/>
              <text x={x+20} y={115} fontSize="10" fill={color} fontWeight="700">a</text>
              <text x={x+w-24} y={115} fontSize="10" fill={color} fontWeight="700">b</text>
            </>}
            {shape==='rect' && <>
              <rect x={x+30} y={64} width={w-60} height={65} rx={3} fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" strokeDasharray="4,2"/>
              {/* CCW arrow on boundary */}
              <path d={`M ${x+w/2},${64} A ${(w-60)/2} ${32} 0 1 0 ${x+w/2-1},${64}`} fill="none" stroke={color} strokeWidth="2"/>
              <polygon points={`${x+w/2},${64} ${x+w/2-8},${70} ${x+w/2+6},${71}`} fill={color}/>
              <text x={x+w/2} y={99} textAnchor="middle" fontSize="10" fill={color} fontWeight="700">R</text>
              <text x={x+w-14} y={72} fontSize="10" fill={color} fontWeight="700">C</text>
            </>}
            {shape==='surf' && <>
              {/* Tilted parallelogram */}
              <polygon points={`${x+35},${118} ${x+100},${118} ${x+w-20},${64} ${x+w-85},${64}`} fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" strokeDasharray="4,2"/>
              <path d={`M ${x+35},${118} Q ${x+w/2},${128} ${x+w-20},${118}`} fill="none" stroke={color} strokeWidth="2"/>
              <polygon points={`${x+w-20},${118} ${x+w-30},${112} ${x+w-28},${122}`} fill={color}/>
              <text x={x+w/2} y={96} textAnchor="middle" fontSize="10" fill={color} fontWeight="700">S</text>
              <text x={x+w-14} y={128} fontSize="10" fill={color} fontWeight="700">C</text>
            </>}
            {shape==='sphere' && <>
              <circle cx={x+w/2} cy={99} r={36} fill={color} fillOpacity="0.12" stroke={color} strokeWidth="2"/>
              <ellipse cx={x+w/2} cy={99} rx={36} ry={10} fill="none" stroke={color} strokeWidth="1" strokeDasharray="4,2" opacity="0.5"/>
              <text x={x+w/2} y={102} textAnchor="middle" fontSize="10" fill={color} fontWeight="700">V</text>
              <text x={x+w/2+36} y={88} fontSize="10" fill={color} fontWeight="700">S</text>
            </>}

            {/* Title */}
            <text x={x+w/2} y={162} textAnchor="middle" fontSize="14" fill={color} fontWeight="700">{title}</text>
            {/* Formula */}
            <rect x={x+8} y={170} width={w-16} height={54} rx={3} fill="#fff" fillOpacity="0.7"/>
            <text x={x+w/2} y={186} textAnchor="middle" fontSize="9.5" fill="#1F3864" fontFamily="'Courier New',monospace" fontWeight="700">{sub.split('\n')[0]}</text>
            {sub.includes('\n') && <text x={x+w/2} y={200} textAnchor="middle" fontSize="9.5" fill="#1F3864" fontFamily="'Courier New',monospace" fontWeight="700">{sub.split('\n')[1]}</text>}
            {/* Description */}
            {desc.split('\n').map((ln,i)=>(
              <text key={i} x={x+w/2} y={234+i*16} textAnchor="middle" fontSize="11" fill="#444">{ln}</text>
            ))}
            {/* Dimension label */}
            <rect x={x+8} y={H-62} width={w-16} height={20} rx={3} fill={color} fillOpacity="0.15"/>
            <text x={x+w/2} y={H-49} textAnchor="middle" fontSize="10" fill={color} fontWeight="700">{dim}</text>

            {/* Arrow between cards */}
            {x < 656 && (
              <text x={x+w+3} y={H/2+6} textAnchor="middle" fontSize="22" fill="#1F3864" opacity="0.3">→</text>
            )}
          </g>
        ))}

        {/* Bottom unifying note */}
        <rect x={14} y={H-28} width={872} height={22} rx={3} fill="#1F3864" fillOpacity="0.05"/>
        <text x={450} y={H-14} textAnchor="middle" fontSize="11" fill="#555" fontStyle="italic">
          Each theorem reduces dimension by 1: integration over an n-dim object = evaluation on its (n−1)-dim boundary
        </text>
      </svg>
      <div style={S.dCaption}>
        All four theorems are instances of the Generalised Stokes' Theorem: the integral of a differential form over an oriented manifold equals the integral over its boundary. FTC handles 1D, Green's handles 2D regions, Stokes handles 3D surfaces, and the Divergence Theorem handles 3D volumes. Recognising this pattern makes each theorem intuitive rather than isolated.
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────
export default function VectorCalculus() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Vector Calculus</h1>
      <p style={S.p}>
        Vector calculus extends differentiation and integration to vector fields — quantities that have both magnitude and direction at every point in space. While multivariable calculus asks "how does a scalar function change?", vector calculus asks "how does a field flow, rotate, and spread through space?" It is the direct mathematical language of electromagnetism, fluid dynamics, heat transfer, and structural mechanics.
      </p>

      <InfoBox title="Core Tools: Three operations on fields, three major theorems" items={[
        "Gradient ∇f — turns a scalar field into a vector field (steepest ascent direction)",
        "Divergence ∇·F — turns a vector field into a scalar (net outflow per unit volume)",
        "Curl ∇×F — turns a vector field into a vector field (local rotation axis & rate)",
        "Divergence Theorem: ∯F·dS = ∭∇·F dV — flux through surface = divergence over volume",
        "Stokes' Theorem: ∮F·dr = ∬(∇×F)·dS — circulation = curl through bounded surface",
        "Conservative field: ∇×F = 0 ↔ F = ∇φ ↔ work is path-independent",
      ]}/>

      <h2 style={S.h2}>The Del Operator — Gradient, Divergence & Curl</h2>
      <DiagramDelOperator/>
      <Table2 rows={[
        ["Del operator ∇","∇ = (∂/∂x, ∂/∂y, ∂/∂z)\nA vector of partial derivative operators — not a vector itself"],
        ["Gradient  ∇f","∇f = (∂f/∂x, ∂f/∂y, ∂f/∂z)\nScalar → vector. Points in direction of steepest increase of f"],
        ["Divergence  ∇·F","∇·F = ∂P/∂x + ∂Q/∂y + ∂R/∂z\nVector → scalar. Measures net outflow per unit volume"],
        ["Curl  ∇×F","∇×F = (∂R/∂y−∂Q/∂z, ∂P/∂z−∂R/∂x, ∂Q/∂x−∂P/∂y)\nVector → vector. Measures local rotation / angular velocity"],
        ["Laplacian  ∇²f","∇²f = ∂²f/∂x² + ∂²f/∂y² + ∂²f/∂z²\n∇·(∇f) — appears in heat equation, wave equation, Poisson eq."],
        ["Vector Laplacian  ∇²F","∇²F = (∇²P, ∇²Q, ∇²R)\nApplied component-wise; appears in Navier-Stokes equations"],
        ["Irrotational field","∇×F = 0 → no local rotation\nNecessary condition for F to be conservative"],
        ["Solenoidal / divergence-free","∇·F = 0 → no sources or sinks\nIncompressible fluid flow; magnetic field B is always solenoidal"],
        ["Vector identities","∇×(∇f) = 0  (curl of gradient is always zero)\n∇·(∇×F) = 0  (divergence of curl is always zero)"],
      ]}/>

      <h2 style={S.h2}>Surface Integrals & Flux</h2>
      <DiagramSurfaceIntegral/>
      <Table2 rows={[
        ["Parametric surface","r(u,v) = (x(u,v), y(u,v), z(u,v))\nDescribes a surface by mapping a 2D parameter domain to 3D space"],
        ["Tangent vectors","r_u = ∂r/∂u,  r_v = ∂r/∂v\nSpan the tangent plane at each point on S"],
        ["Surface area element","dS = |r_u × r_v| du dv\nScalar area of an infinitesimal patch"],
        ["Scalar surface integral","∬_S f dS = ∬_D f(r(u,v)) |r_u×r_v| du dv\nIntegrates scalar quantity (e.g. temperature, density) over area"],
        ["Unit normal","n̂ = (r_u × r_v) / |r_u × r_v|\nPerpendicular to surface; choice of sign = orientation"],
        ["Vector surface integral (flux)","∬_S F·dS = ∬_S F·n̂ dS\nMeasures net flow of F through S per unit time"],
        ["Flux through sphere","∯_S F·dS where S encloses volume V\nPositive = net outflow; negative = net inflow"],
        ["Mass flow rate","ṁ = ∬_S ρv·n̂ dS\nDensity × velocity flux through cross-section — used in CFD"],
      ]}/>

      <h2 style={S.h2}>Divergence Theorem</h2>
      <DiagramDivergenceTheorem/>
      <Table2 rows={[
        ["Statement","∯_S F·dS = ∭_V (∇·F) dV\nS = closed surface bounding volume V; outward normal convention"],
        ["Physical meaning","Total flux out of a closed surface = total divergence inside\nIf div F = 0 everywhere inside, net flux = 0"],
        ["Gauss's Law (E-field)","∯_S E·dS = Q_enc / ε₀\nTotal electric flux through closed surface = enclosed charge / ε₀"],
        ["Gauss's Law (B-field)","∯_S B·dS = 0\nNo magnetic monopoles — B is always solenoidal"],
        ["Continuity (fluids)","∂ρ/∂t + ∇·(ρv) = 0\nDivergence theorem → conservation of mass in control volume"],
        ["Heat conduction","∂T/∂t = α ∇²T\nDivergence theorem applied to heat flux q = −k∇T gives this PDE"],
        ["Incompressible flow","∇·v = 0  (divergence-free velocity field)\nNet mass flux through any closed surface in the fluid = 0"],
        ["Engineering use","Convert hard surface integrals to easier volume integrals (or vice versa)\nChoose whichever side of the equation is simpler to evaluate"],
      ]}/>

      <h2 style={S.h2}>Stokes' Theorem</h2>
      <DiagramStokesTheorem/>
      <Table2 rows={[
        ["Statement","∮_C F·dr = ∬_S (∇×F)·dS\nC = boundary curve of oriented surface S"],
        ["Physical meaning","Circulation of F around C = total curl (rotation) through S\nIf curl F = 0, circulation around any closed curve = 0"],
        ["Right-hand rule","Curl fingers in direction of C; thumb gives positive normal n̂\nSign of integral depends on consistent orientation"],
        ["Faraday's Law","∮_C E·dr = −d/dt ∬_S B·dS\nCirculation of E = negative rate of change of magnetic flux"],
        ["Ampère's Law","∮_C B·dr = μ₀ ∬_S J·dS\nCirculation of B around loop = current through bounded surface"],
        ["Conservative field test","∇×F = 0 everywhere in simply connected domain\n→ F is conservative; F = ∇φ for some scalar potential φ"],
        ["Path independence","F conservative ↔ ∮_C F·dr = 0 for all closed C\n↔ work done depends only on start and end points"],
        ["Green's Theorem (2D case)","Stokes with flat surface in xy-plane, n̂ = k̂\n∮_C (P dx + Q dy) = ∬_R (∂Q/∂x − ∂P/∂y) dA"],
      ]}/>

      <h2 style={S.h2}>The Four Fundamental Theorems</h2>
      <DiagramFundamentalTheorems/>
      <Table2 rows={[
        ["FTC (1D)","∫_a^b f'(x)dx = f(b) − f(a)\nIntegral of derivative = difference of boundary values"],
        ["Green's Theorem (2D)","∮_C F·dr = ∬_R (∂Q/∂x − ∂P/∂y) dA\nBoundary = closed curve C; interior = region R"],
        ["Stokes' Theorem (3D surface)","∮_C F·dr = ∬_S (∇×F)·dS\nBoundary = curve C; interior = surface S"],
        ["Divergence Theorem (3D volume)","∯_S F·dS = ∭_V (∇·F) dV\nBoundary = closed surface S; interior = volume V"],
        ["Unifying pattern","∫_Ω dω = ∮_∂Ω ω  (Generalised Stokes)\nIntegral of derivative over Ω = integral over boundary ∂Ω"],
        ["Dimension reduction","Each theorem reduces integral dimension by 1\n3D volume → 2D surface → 1D curve → 0D boundary (points)"],
      ]}/>

      <InfoBox title="Key Takeaway — Which Theorem to Reach For" items={[
        "Need flux through a closed surface? → Divergence Theorem: convert to volume integral of div F",
        "Need circulation around a closed curve? → Stokes' Theorem: convert to surface integral of curl F",
        "Is the field conservative? → Check ∇×F = 0; if yes, find potential φ and use FTC",
        "Working in 2D with a region and boundary? → Green's Theorem is Stokes in the plane",
        "Field has no sources/sinks (∇·F=0)? → Net flux through any closed surface is zero",
        "Field is irrotational (∇×F=0)? → Circulation around any closed loop is zero",
        "Heat, pressure, or concentration diffusing? → Laplacian ∇²f governs the steady state",
        "Fluid assumed incompressible? → Enforce ∇·v = 0 as the constraint",
      ]}/>
    </div>
  );
}